import api from '@/lib/api';
import type { PaymentType } from '@/services/api/frontdesk.api';

export interface BookingsQuery {
  unitId?: string;
  propertyId?: string;
  status?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

export interface CalendarQuery {
  propertyId?: string;
  from?: string;
  to?: string;
}

export interface CreateBookingPayload {
  unitId: string;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  source: string;
  guestData: {
    name: string;
    email?: string;
    phone?: string;
  };
  specialRequests?: string;
  /** TASK-68: optional promo code applied at booking create. */
  promoCode?: string;
  /** TASK-68: optional rate plan; threaded for deposit-policy linkage. */
  ratePlanId?: string;
}

/** TASK-68: payload for the staff cancel action. */
export interface CancelBookingPayload {
  reason?: string;
  reasonCodeId?: string;
}

export interface BookingUnit {
  id: string;
  name: string;
  type?: string;
}

export interface BookingGuest {
  id: string;
  fullName: string;
  email: string | null;
}

export interface Booking {
  id: string;
  unitId: string;
  guestId: string | null;
  source: string;
  status: string;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  baseAmount: string;
  totalAmount: string;
  discountAmount?: string | null;
  depositDueAmount?: string | null;
  depositDueDate?: string | null;
  currency: string;
  /** How the booking will be settled; null = not yet classified. */
  paymentType?: PaymentType | null;
  specialRequests: string | null;
  /**
   * OTA booking reference (channel-manager `otaRefId` / external id). Present on
   * channel-sourced bookings; null for direct/manual.
   */
  externalBookingId?: string | null;
  /**
   * Full raw channel-manager (AxisRooms API-9) payload retained verbatim —
   * carries the OTA identity + per-night `dailyPrice`. Returned by
   * `GET /bookings/:id` (E9); absent on the lean list. `null` for direct.
   */
  channelPayload?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt: string;
  unit?: BookingUnit | null;
  guest?: BookingGuest | null;
}

export interface UpdateBookingPayload {
  checkIn?: string;
  checkOut?: string;
  guestsCount?: number;
  source?: string;
  specialRequests?: string;
}

export async function fetchBookings(query?: BookingsQuery) {
  const { data } = await api.get('/bookings', { params: query });
  return data;
}

/**
 * E9 — GET /bookings/:id. Returns the raw booking incl. `channelPayload`,
 * `source` and `externalBookingId` (the lean list omits `channelPayload`), so
 * the bill can surface the OTA source line + per-night rate without fattening
 * the whole list.
 */
export async function getBooking(id: string): Promise<Booking> {
  const { data } = await api.get<Booking>(`/bookings/${id}`);
  return data;
}

export async function fetchCalendar(query?: CalendarQuery) {
  const { data } = await api.get('/bookings/calendar', { params: query });
  return data;
}

export async function createBooking(payload: CreateBookingPayload) {
  const { data } = await api.post('/bookings', payload);
  return data;
}

export async function updateBooking(id: string, payload: UpdateBookingPayload) {
  const { data } = await api.patch(`/bookings/${id}`, payload);
  return data;
}

export async function cancelBooking(
  id: string,
  payload: CancelBookingPayload = { reason: 'Cancelled by user' },
) {
  const body: CancelBookingPayload = {};
  if (payload.reason !== undefined) body.reason = payload.reason;
  if (payload.reasonCodeId !== undefined) body.reasonCodeId = payload.reasonCodeId;
  const { data } = await api.post(`/bookings/${id}/cancel`, body);
  return data;
}

export async function checkInBooking(id: string, payload?: { notes?: string; idDocumentUrl?: string; actualCheckInTime?: string }) {
  const { data } = await api.post(`/bookings/${id}/check-in`, payload ?? {});
  return data;
}

export async function checkOutBooking(id: string, payload?: { notes?: string; additionalCharges?: number; actualCheckOutTime?: string }) {
  const { data } = await api.post(`/bookings/${id}/check-out`, payload ?? {});
  return data;
}

export interface WalkInPayload {
  unitId: string;
  guestData: { name: string; email?: string; phone?: string };
  checkIn?: string;
  checkOut: string;
  guestsCount: number;
  rate: number;
  notes?: string;
}

export async function createWalkIn(payload: WalkInPayload) {
  const { data } = await api.post('/bookings/walk-in', payload);
  return data;
}

// ─── Room Move ──────────────────────────────────────────────────────────────

export interface MoveBookingPayload {
  newUnitId: string;
  reason?: string;
}

export async function moveBookingRoom(id: string, payload: MoveBookingPayload): Promise<Booking> {
  const { data } = await api.post<Booking>(`/bookings/${id}/move`, payload);
  return data;
}

// ─── Stay Extension ─────────────────────────────────────────────────────────

export interface ExtendStayPayload {
  newCheckOut: string;
}

export async function extendBookingStay(id: string, payload: ExtendStayPayload): Promise<Booking> {
  const { data } = await api.post<Booking>(`/bookings/${id}/extend`, payload);
  return data;
}
